import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PERMISSIONS } from '@lims/permissions';
import { samplesApi } from '../../api/endpoints.js';
import type { Sample, SamplePiece } from '../../api/endpoints.js';
import { useAuth } from '../../app/auth-context.js';
import {
  ActionBar,
  ActionButton,
  Alert,
  DataTable,
  Dialog,
  NumberField,
  Panel,
  TextAreaField,
  TextField,
  useToast,
  type DataColumn,
} from '../../components/ui/index.js';

/**
 * The pieces of this sample, if anybody recorded any.
 *
 * ===========================================================================
 * PIECES ARE OPTIONAL AND NEVER IMPLIED (decision D74).
 * ===========================================================================
 *
 * Nothing about a material's type tells this system how many pieces arrived. No
 * count is suggested, defaulted or pre-filled for concrete, for steel or for
 * anything else — suggesting "6 cubes" would be inventing a method's specimen
 * requirement, which is exactly what section 4 of CLAUDE.md forbids. A sample
 * has the pieces somebody recorded, and none until then.
 *
 * SO ZERO PIECES IS NOT "ONE PIECE". The empty state says nobody has recorded
 * any, which is a different statement from the sample being indivisible.
 *
 * PIECE CODES ARE DERIVED FROM THE SAMPLE NUMBER by the backend and cannot be
 * typed here. A piece found on a bench then names its parent without a lookup,
 * and inherits the guarantee that sample numbers are never reused.
 *
 * A PIECE IS NEVER DELETED. It can be corrected — its label, its note, where it
 * is kept — and the database refuses a delete outright, because a piece that
 * existed and then vanished from the record is a hole in the traceability the
 * phase exists to provide.
 */
export function SamplePiecesPanel({
  sample,
  onLabel,
}: {
  readonly sample: Sample;
  /** Opens the label preview for one piece. */
  readonly onLabel: (pieceId: string) => void;
}) {
  const { t } = useTranslation();
  const { can } = useAuth();
  const { show } = useToast();
  const queryClient = useQueryClient();

  const [isAdding, setIsAdding] = useState(false);
  const [count, setCount] = useState<number | null>(null);
  const [label, setLabel] = useState('');
  const [notes, setNotes] = useState('');

  const [editing, setEditing] = useState<SamplePiece | null>(null);
  const [editLabel, setEditLabel] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [editLocation, setEditLocation] = useState('');

  const pieces = useQuery({
    queryKey: ['sample', sample.id, 'pieces'],
    queryFn: () => samplesApi.pieces(sample.id),
  });

  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: ['sample', sample.id] });
    await queryClient.invalidateQueries({ queryKey: ['samples'] });
  };

  const add = useMutation({
    mutationFn: () =>
      samplesApi.addPieces(sample.id, {
        count,
        ...(label.trim() ? { label: label.trim() } : {}),
        ...(notes.trim() ? { notes: notes.trim() } : {}),
      }),
    onSuccess: async () => {
      await invalidate();
      show({ tone: 'success', message: t('samples.piecesRecorded') });
      setIsAdding(false);
      setCount(null);
      setLabel('');
      setNotes('');
    },
  });

  const correct = useMutation({
    mutationFn: () =>
      samplesApi.updatePiece(sample.id, editing?.id ?? '', {
        label: editLabel.trim() === '' ? null : editLabel.trim(),
        notes: editNotes.trim() === '' ? null : editNotes.trim(),
        storageLocation:
          editLocation.trim() === '' ? null : editLocation.trim(),
      }),
    onSuccess: async () => {
      await invalidate();
      show({ tone: 'success', message: t('samples.pieceCorrected') });
      setEditing(null);
    },
  });

  // A voided sample takes no further work (D211): its pieces are read-only.
  const mayEdit = can(PERMISSIONS.SAMPLES_UPDATE) && !sample.isDisposed && !sample.isVoided;

  const columns: readonly DataColumn<SamplePiece>[] = [
    {
      key: 'pieceCode',
      header: t('samples.pieceCode'),
      render: (piece) => (
        // Derived by the backend from the sample number. A machine identifier,
        // so left-to-right in both languages.
        <span className="font-medium text-default-900" dir="ltr">
          {piece.pieceCode}
        </span>
      ),
    },
    {
      key: 'label',
      header: t('samples.pieceLabel'),
      render: (piece) =>
        piece.label ? (
          <span className="text-default-700">{piece.label}</span>
        ) : (
          <span className="text-caption text-default-600">
            {t('common.notRecorded')}
          </span>
        ),
    },
    {
      key: 'storageLocation',
      header: t('samples.storageLocation'),
      render: (piece) =>
        piece.storageLocation ? (
          <span className="text-default-700">{piece.storageLocation}</span>
        ) : (
          <span className="text-caption text-default-600">
            {t('common.notRecorded')}
          </span>
        ),
    },
    {
      key: 'notes',
      header: t('samples.notes'),
      render: (piece) =>
        piece.notes ? (
          <span className="text-caption text-default-600">{piece.notes}</span>
        ) : null,
    },
    {
      key: 'actions',
      header: t('common.actions'),
      isHeaderHidden: true,
      align: 'end',
      render: (piece) => (
        <ActionBar>
          {can(PERMISSIONS.LABELS_PRINT) && !sample.isVoided ? (
            <ActionButton
              priority="tertiary"
              size="sm"
              onPress={() => onLabel(piece.id)}
            >
              {t('samples.labelAction')}
            </ActionButton>
          ) : null}
          {mayEdit ? (
            <ActionButton
              priority="tertiary"
              size="sm"
              onPress={() => {
                setEditing(piece);
                setEditLabel(piece.label ?? '');
                setEditNotes(piece.notes ?? '');
                setEditLocation(piece.storageLocation ?? '');
              }}
            >
              {t('common.edit')}
            </ActionButton>
          ) : null}
        </ActionBar>
      ),
    },
  ];

  return (
    <Panel
      title={t('samples.piecesTitle')}
      description={t('samples.piecesHint')}
      actions={
        mayEdit ? (
          <ActionButton
            priority="secondary"
            size="sm"
            onPress={() => setIsAdding(true)}
          >
            {t('samples.addPieces')}
          </ActionButton>
        ) : null
      }
    >
      <DataTable
        label={t('samples.piecesTitle')}
        columns={columns}
        rows={pieces.data ?? []}
        rowKey={(piece) => piece.id}
        isLoading={pieces.isLoading}
        error={pieces.error}
        onRetry={() => void pieces.refetch()}
        // Nobody recorded any. NOT "this sample is a single piece".
        emptyContent={t('samples.noPiecesRecorded')}
      />

      <p className="mt-stack text-caption text-default-600">
        {t('samples.piecesNeverDeletedNotice')}
      </p>

      <Dialog
        isOpen={isAdding}
        onClose={() => setIsAdding(false)}
        title={t('samples.addPieces')}
        footer={
          <ActionBar>
            <ActionButton
              priority="tertiary"
              onPress={() => setIsAdding(false)}
              isDisabled={add.isPending}
            >
              {t('common.cancel')}
            </ActionButton>
            <ActionButton
              priority="primary"
              isDisabled={count === null || count < 1}
              isLoading={add.isPending}
              onPress={() => add.mutate()}
            >
              {t('common.add')}
            </ActionButton>
          </ActionBar>
        }
      >
        <div className="flex flex-col gap-stack">
          {/*
            Empty, with no suggested count. The number of pieces is a fact about
            what physically arrived, and this product does not guess it.
          */}
          <NumberField
            label={t('samples.pieceCount')}
            description={t('samples.pieceCountHint')}
            value={count}
            onChange={setCount}
            isRequired
            step="1"
          />
          <TextField
            label={t('samples.pieceLabel')}
            description={t('samples.pieceLabelHint')}
            value={label}
            onChange={setLabel}
          />
          <TextAreaField
            label={t('samples.notes')}
            value={notes}
            onChange={setNotes}
            rows={2}
          />
          {add.error ? (
            <Alert tone="danger" title={t('samples.piecesFailed')} />
          ) : null}
          <p className="text-caption text-default-600">
            {t('samples.pieceCodeDerivedNotice', {
              count: count ?? 0,
              sampleNumber: sample.sampleNumber,
            })}
          </p>
        </div>
      </Dialog>

      <Dialog
        isOpen={editing !== null}
        onClose={() => setEditing(null)}
        title={t('samples.correctPiece')}
        footer={
          <ActionBar>
            <ActionButton
              priority="tertiary"
              onPress={() => setEditing(null)}
              isDisabled={correct.isPending}
            >
              {t('common.cancel')}
            </ActionButton>
            <ActionButton
              priority="primary"
              isLoading={correct.isPending}
              onPress={() => correct.mutate()}
            >
              {t('common.save')}
            </ActionButton>
          </ActionBar>
        }
      >
        <div className="flex flex-col gap-stack">
          <p className="text-caption text-default-600" dir="ltr">
            {editing?.pieceCode}
          </p>
          <TextField
            label={t('samples.pieceLabel')}
            value={editLabel}
            onChange={setEditLabel}
          />
          <TextField
            label={t('samples.storageLocation')}
            value={editLocation}
            onChange={setEditLocation}
          />
          <TextAreaField
            label={t('samples.notes')}
            value={editNotes}
            onChange={setEditNotes}
            rows={2}
          />
          {correct.error ? (
            <Alert tone="danger" title={t('samples.pieceCorrectionFailed')} />
          ) : null}
          <p className="text-caption text-default-600">
            {t('samples.pieceCodeImmutableNotice')}
          </p>
        </div>
      </Dialog>
    </Panel>
  );
}
